import React from "react";
import { GetTable } from "../../common/components/DataTable"
import DatesHelper from "../../common/datesHelper";
import { InterventoDto, TechnicianDto } from "../../generated";
import InterventiActions from "../InterventiActions";
import TechnicianViewer from "../../technicians/components/TechnicianViewer";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { attach } from "../../common/componentExtensions";
import InterventiReportStore from "../InterventiReportStore"
import Period from "../../common/period";
import PeriodInput from "../../common/components/periodInput";
import { InterventoChecksLabels } from "./interventiClienteWidget";
import { CustomerData } from "../../customers/components/CustomerData";
import { links } from "../../routes";
import { Link } from "../../common/components/link";
import { Loading } from "../../common/components/loading";
import TechiniciansStore from "../../technicians/TechiniciansStore";
import { TechniciansFilter } from "../../technicians/components/TechniciansFilter";
import { addressDescription } from "../../customers/components/customerForm";
import { PhoneComponents } from "../../common/components/PhoneComponent";
import { ImpiantoAddressDescription, ImpiantoLightDataComponent } from "../../impianti/components/impiantoLightData";

const Table = GetTable<InterventoDto>();

interface Props extends RouteComponentProps<{ from: string, to: string }> {
    period: Period;
    interventi: InterventoDto[];
}

interface State {
    selectedTechnicians: TechnicianDto[] | null;
    selectedInterventi: InterventoDto[];
}

const InterventiReportPage: React.FunctionComponent<Props> = ({ period, interventi, location }) => {
    const history = useHistory();
    const [state, setState] = React.useState<State>({ selectedTechnicians: null, selectedInterventi: [] });

    React.useEffect(() => {
        const stored = InterventiReportStore.getForPeriod(period);
        if (!Array.isArray(stored)) {
            InterventiActions.getReportInterventi(period.from, period.to);
        }
    }, [period.from, period.to]);

    const allTechnicians = TechiniciansStore.useAll();

    if (allTechnicians == null)
        return <Loading />

    const filterdInterventi = allTechnicians != null && allTechnicians.length > 0
        ? interventi.filter(i => i.idTechnician == null || (state.selectedTechnicians != null ? state.selectedTechnicians : allTechnicians).some(t => t.id === i.idTechnician))
        : interventi;

    return (
        <div className="card">
            <div className="card-header bg-theme text-white">
                <h3 className="text-white">Report interventi dal {DatesHelper.getReadableDateString(period.from)} al {DatesHelper.getReadableDateString(period.to)}</h3>
            </div>
            <div className="card-body">
                <div className="row mb-2">
                    <div className="col-12 col-md-6">
                        <PeriodInput
                            labelFrom="Interventi dal"
                            labelTo="Interventi al"
                            period={period}
                            onChange={period => {
                                history.push({
                                    pathname: location.pathname,
                                    search: "?" + new URLSearchParams({ from: DatesHelper.compactDateString(period.from), to: DatesHelper.compactDateString(period.to) }).toString()
                                })
                            }}
                        />
                    </div>
                    <div className="col-12 col-md-6">
                        <TechniciansFilter
                            label="Filtro tecnici"
                            selected={state.selectedTechnicians || allTechnicians}
                            onChange={selectedTechnicians => setState({ ...state, selectedTechnicians })}
                        />
                    </div>
                </div>
                <Table
                    keySelector={x => x.idIntervento.toString()}
                    filterables={[
                        x => x.dettaglioIntervento,
                        x => x.oggettoIntervento,
                        x => x.tipoIntervento.descTipoIntervento,
                        x => DatesHelper.getReadableDateString(x.dataIntervento),
                    ]}
                    data={filterdInterventi}
                >
                    <Table.SelectColumn
                        selectedItems={state.selectedInterventi}
                        onSelectionChanged={selectedInterventi => setState({ ...state, selectedInterventi })}
                        disabled={x => x.rapportoControlloId == null}
                    />
                    <Table.Column
                        header="Data"
                        orderable
                        template={intervento => <div>
                            <div>{DatesHelper.getReadableDateString(intervento.dataIntervento)}</div>
                            <div style={{ whiteSpace: "nowrap" }}>
                                {intervento.dataIntervento.getHours() > 0 && <span>{DatesHelper.getReadableTimeString(intervento.dataIntervento)}</span>}
                                {intervento.dataFineIntervento && <span> - {DatesHelper.getReadableTimeString(intervento.dataFineIntervento)}</span>}
                            </div>
                        </div>}
                        groupBy={x => DatesHelper.getReadableDateString(x.dataIntervento)}
                        orderBy={x => x.dataIntervento.valueOf()}
                        defaultGroupingIndex={0}
                    />
                    <Table.Column
                        header="Tecnico"
                        template={intervento => <TechnicianViewer
                            id={intervento.idTechnician}
                            template={x => <span style={{ whiteSpace: "nowrap" }}><i className="fas fa-circle fa-lg" style={{ color: x.color }} /> {x.firstName} {x.lastName}</span>}
                        />}
                        orderBy={x => x.idTechnician || ""}
                        groupBy={x => x.idTechnician || ""}
                        groupTemplate={key => key && <TechnicianViewer
                            id={key}
                            template={x => <span style={{ whiteSpace: "nowrap" }}>
                                <i className="fas fa-circle fa-lg" style={{ color: x.color }} /> {x.firstName} {x.lastName}
                            </span>
                            }
                        /> || "Tecnico non assegnato"}
                    />
                    <Table.Column
                        header="Cliente"
                        template={intervento => intervento.idCliente && <CustomerData
                            id={intervento.idCliente}
                            template={i => {
                                const nomeCognome = `${i.cognomeProprietario} ${i.nomeProprietario}`;
                                return (
                                    <div className="d-flex flex-column">
                                        <Link to={links.customerShow(i.idCliente)}>
                                            <div>{nomeCognome}</div>
                                            {i.responsabile && i.responsabile != nomeCognome && <div>{i.responsabile}</div>}
                                        </Link>
                                    </div>);
                            }}
                        />
                        }
                    />
                    <Table.Column
                        header="Indirizzo"
                        orderable
                        groupable={false}
                        template={intervento =>
                            <CustomerData id={intervento.idCliente}
                                template={customer =>
                                    intervento.idImpianto
                                    && <ImpiantoAddressDescription
                                        customer={customer}
                                        idImpianto={intervento.idImpianto}
                                    /> ||
                                    <div>{addressDescription(customer)} {customer.piano && <span className="ms-3">Piano: <strong>{customer.piano}</strong></span>}</div>
                                } />
                        }
                    />
                    <Table.Column
                        header="Tel"
                        template={intervento => intervento.idCliente && <CustomerData
                            id={intervento.idCliente}
                            template={i =>
                                <div>
                                    {i.cell && <PhoneComponents tel={i.cell} showWhatsApp />}
                                    {i.telefono && <PhoneComponents tel={i.telefono} />}
                                </div>}
                        />}
                    />
                    <Table.Column
                        header="Impianto"
                        orderable groupable
                        template={intervento => intervento.idImpianto
                            && <ImpiantoLightDataComponent
                                id={intervento.idImpianto}
                                idCliente={intervento.idCliente}
                                template={c => <div> {c.marca} {c.modello}</div>}
                            />}
                    />
                    <Table.Column
                        header="Oggetto Intervento"
                        orderable groupable
                        template={intervento => intervento.oggettoIntervento}
                    />
                    <Table.Column
                        header="Manutenzione"
                        orderable groupable
                        template={intervento => <InterventoChecksLabels intervento={intervento} />}
                    />
                    <Table.Column
                        header="Descrizione"
                        orderable groupable
                        template={intervento => intervento.tipoIntervento?.descTipoIntervento}
                    />
                </Table>
                {state.selectedInterventi.length > 0 && <div className="d-none d-md-flex w-100 card sticky-top" style={{
                    position: "sticky",
                    bottom: 10,
                    background: "white",
                    zIndex: 2,
                }}>
                    <div className="w-100 card-body">
                        <button
                            className="btn btn-outline-secondary me-2"
                            onClick={async () => {
                                await InterventiActions.downloadCatastoEsa(state.selectedInterventi.map(x => x.idIntervento))
                            }}>
                            <i className="fas fa-file-export me-2" /> Export catasto ESA
                        </button>
                        <button
                            className="btn btn-outline-secondary me-2"
                            onClick={async () => {
                                const data = prompt("Inserisci il tuo codice azienda siert");
                                await InterventiActions.downloadCatastoSiert(state.selectedInterventi.map(x => x.idIntervento), data && parseInt(data) || 1)
                            }}>
                            <i className="fas fa-file-export me-2" /> Export catasto Siert
                        </button>
                    </div>
                </div>}
                {state.selectedInterventi.length > 0 && <div className="d-flex d-md-none w-100 card sticky-bottom" style={{
                    background: "white",
                    zIndex: 2,
                }}>
                    <div className="card-footer d-flex flex-wrap justify-content-around">

                        <button className="btn btn-outline-primary d-flex flex-column align-items-center justify-content-center"
                            onClick={async () => {
                                await InterventiActions.downloadCatastoEsa(state.selectedInterventi.map(x => x.idIntervento))
                            }} style={{ width: 52, height: 52 }}>
                            <h3 className="fas fa-file-export" />
                            <span style={{ fontSize: "0.65em" }} className="text-truncate">Export catasto ESA</span>
                        </button>
                        <button className="btn btn-outline-primary d-flex flex-column align-items-center justify-content-center"
                            onClick={async () => {
                                const data = prompt("Inserisci il tuo codice azienda siert");
                                await InterventiActions.downloadCatastoSiert(state.selectedInterventi.map(x => x.idIntervento), data && parseInt(data) || 1)
                            }} style={{ width: 52, height: 52 }}>
                            <h3 className="fas fa-file-export" />
                            <span style={{ fontSize: "0.65em" }} className="text-truncate">Export catasto Siert</span>
                        </button>
                    </div>
                </div>}
            </div>
        </div>
    );
}


export default attach(InterventiReportPage, InterventiReportStore, (store, props) => {
    const params = new URLSearchParams(props.location.search);
    const today = DatesHelper.Today();
    const from = params.get("from");
    const to = params.get("to");
    const fromDate = from ? DatesHelper.fromCompactDateString(from) : today;
    const toDate = to ? DatesHelper.fromCompactDateString(to) : DatesHelper.AddDays(today, 7);
    const period = Period.Create(fromDate, toDate);
    const stored = store.getForPeriod(period);
    return {
        period,
        interventi: Array.isArray(stored) ? stored : [],
    }
})
